<script>
import CentralConfigStepGuide from '@modules/central-config/components/central-config-step-guide.vue'
import IconWithBackground from '@modules/crm/components/icon-with-background/icon-with-background.vue'
import { lucideToIcon } from '@modules/central-config/utils/icon-map'
import { apiCentralConfig } from '@modules/central-config/api/apiCentralConfig'
import { journeyRefreshMixin } from '@modules/central-config/mixins/journey-refresh-mixin'
import { journeyVisibilityMixin } from '@modules/central-config/mixins/journey-visibility-mixin'
import { GlobalBus } from '@services/bus'
import { aawzDate } from '@utils/dates/aawz-date'
import {
  AUTORIA,
  AUTORIA_SEM_AUTOR,
  VITRINE_TOOLTIP,
  CENTRAL_CONFIG_FOCUS_GUIDE,
  VOLTAR_JORNADA_HEADER,
  VOLTAR_JORNADA_RODAPE,
  VOLTAR_MODULO_HEADER,
  VOLTAR_MODULO_RODAPE,
} from '@modules/central-config/constants/Text'
import {
  GUIDE_ORIGIN_JOURNEY,
  GUIDE_ORIGIN_MODULE,
} from '@modules/central-config/utils/guide-origin'
import { rotaInicialDoModulo } from '@modules/central-config/utils/module-routes'
import {
  essentialTasksNaOrdemVisual,
  isEssential,
  moduloAteNivel,
  normalizeNivel,
  tasksNaOrdemVisual,
} from '@modules/central-config/utils/progress'

const HOME_ROUTE = 'central-config.home'
// Duração mínima do spinner de marcar/desmarcar — a API resolve rápido demais
// (<300ms) e o spinner pisca sem o usuário perceber que algo aconteceu.
const MIN_LOADING_MS = 600
// Duração do spinner de navegação (anterior/próxima) — cobre o carregamento
// do chunk lazy-loaded da rota de destino, que não tem callback de conclusão.
const NAVIGATING_DELAY_MS = 500
// Respiro entre marcar a etapa e avançar para a próxima. O spinner do checkbox
// ocupa os MIN_LOADING_MS inteiros, então sem esta pausa o check verde nunca
// chega a ser desenhado: a etapa trocaria no mesmo quadro em que o spinner sai,
// e a conclusão — o que o usuário acabou de pedir — passaria despercebida.
// Menor que NAVIGATING_DELAY_MS de propósito: o guard de navegação em curso
// ainda cobre a janela inteira contra um "Próxima etapa" clicado por cima.
const AUTO_ADVANCE_DELAY_MS = 450

export default {
  name: 'CentralConfigGuideDrawer',
  // BaseLoadingSkeleton NÃO precisa import/registro local — já é global via
  // src/components/_globals.js (require.context sobre _base-*.vue).
  components: { CentralConfigStepGuide, IconWithBackground },
  mixins: [journeyRefreshMixin, journeyVisibilityMixin],
  data() {
    return {
      // Loading da marcação manual da etapa (evita duplo clique no checkbox).
      marking: false,
      // Load do contexto quando o guia é restaurado fora da home (needsJourneyLoad).
      loadingContext: false,
      loadError: false,
      // Navegação anterior/próxima (rotas lazy-loaded de outros módulos).
      navigating: false,
      vitrineTooltip: VITRINE_TOOLTIP,
    }
  },
  computed: {
    // Guia ativo (painel OU FAB): enquanto o usuário executa a jornada, mantém a
    // sincronia com o backend (foco + poll). Para quando o guia fecha.
    guideActive() {
      return this.panelOpen || this.fabOpen
    },
    // Guia ativo (painel ou FAB) porém sem contexto resolvido — a jornada ainda
    // não carregou (ex.: reload fora da home). Dispara o load no created().
    needsJourneyLoad() {
      return (this.panelOpen || this.fabOpen) && !this.hasContext
    },
    // Painel lateral: guia ativo e NÃO recolhido.
    panelOpen() {
      return !!this.$store.getters['centralConfig/guidePanelOpen']
    },
    // FAB (bolinha): guia ativo e recolhido.
    fabOpen() {
      return !!this.$store.getters['centralConfig/guideFabOpen']
    },
    context() {
      return this.$store.getters['centralConfig/guideContext'] || null
    },
    hasContext() {
      return !!this.context
    },
    module() {
      return (this.context && this.context.module) || null
    },
    task() {
      return (this.context && this.context.task) || null
    },
    // Posição da etapa DENTRO do seu grupo (obrigatórias vs opcionais) — o
    // contador "Etapa X de Y" é por grupo, não sobre o total do módulo (o
    // índice cru sobre as 21 etapas mostrava "3 de 21" quando o usuário clicou
    // na 1ª opcional). A navegação anterior/próxima NÃO muda: é global.
    //
    // ★ Contado sobre o catálogo VISÍVEL, não sobre o cru do store (2026-08-05,
    // achado do review do PR #1854). `guideContext` é um getter Vuex, e getter é
    // `(state) => …`: ele não tem o `router` que o gate de contrato exige, então
    // enxerga também o que a tela escondeu. O drawer dizia "de 19" ao lado de um
    // accordion listando 14 — a MESMA inconsistência que esta frente do PR existe
    // para corrigir, sobrevivendo no único lugar que não passava pelo mixin.
    //
    // Fallback para o `guideContext`: sem store/router utilizáveis (testes que
    // montam o drawer isolado), o contador volta ao número cru em vez de sumir.
    moduloVisivelDoGuia() {
      if (!this.context) return null
      return (
        this.modulosVisiveis.find((m) => m.id === this.context.module.id) || null
      )
    },
    // A task COM a marca de vitrine. `context.task` vem do getter Vuex, que não
    // conhece as flags — a marca nasce no mixin, sobre o catálogo visível.
    taskVisivel() {
      const modulo = this.moduloVisivelDoGuia
      if (!modulo || !this.task) return null
      const tasks = (Array.isArray(modulo.tasks) && modulo.tasks) || []
      return tasks.find((t) => t.id === this.task.id) || null
    },
    // ★ O drawer também respeita a vitrine (2026-08-05, reportado em tela pelo
    // owner). Marcar a etapa no accordion já estava bloqueado, mas o guia abria
    // por outro caminho — o "Próxima etapa" navega pela lista COMPLETA — e ali o
    // checkbox seguia ativo, gravando conclusão de um recurso não contratado.
    //
    // O passo a passo continua LEGÍVEL de propósito: é justamente o que mostra o
    // que o recurso faz. O que sai é a ação.
    tarefaEhVitrine() {
      const t = this.taskVisivel
      return !!(t && t.metadata && t.metadata.vitrine)
    },
    // Nível de ALCANCE do contador — o corte que o accordion faz ao desenhar a
    // timeline: até este nível a etapa aparece na lista principal; acima dele vai
    // para o bloco colapsado "Quando quiser ir além".
    //
    // É o MAIOR entre o nível do escritório e o da etapa em foco, e os dois
    // termos têm motivo próprio:
    //
    // • nível do ESCRITÓRIO — é ele que define a seção onde o card é listado na
    //   home, e portanto o que a timeline desenha. No CRM medido em tela, o
    //   escritório está no 2 e a timeline lista 10 (níveis 1+2); cortar no nível
    //   da ETAPA daria 6 para uma etapa de nível 1 — um número que não aparece em
    //   canto nenhum da tela.
    // • nível da ETAPA — cobre quem foi alcançado PELO bloco "ir além". Sem ele,
    //   uma etapa de nível 3 com escritório no 2 cairia fora do próprio grupo
    //   (`groupIndex` -1) e o par sumiria da `metaLine`.
    //
    // `taskVisivel` antes de `task`: quem anota `metadata.nivel` é o catálogo, e é
    // dele que sai a lista contra a qual o índice é medido. O `guideContext` pode
    // carregar uma cópia sem a anotação e devolveria o default 1.
    nivelDeAlcance() {
      const task = this.taskVisivel || this.task
      const daEtapa = normalizeNivel(((task && task.metadata) || {}).nivel)
      const doEscritorio = normalizeNivel(
        this.$store.getters['centralConfig/nivelAtualNumero']
      )
      return Math.max(daEtapa, doEscritorio)
    },
    // ★ Conta só o que a pessoa VÊ NA LISTA PRINCIPAL (2026-08-07, reportado em
    // tela pelo owner: "Configurar integração de Ligações é a 3ª da listagem e o
    // drawer diz Etapa 5").
    //
    // São DOIS cortes, e os dois nasceram do mesmo sintoma em tela:
    //
    // 1. `naListaPrincipal` exclui `indisponivel`/`vitrine` — hoje `indisponivel`
    //    nunca chega aqui: a obrigatória barrada por contrato SOME
    //    (`filtrarModulosVisiveis` em `journey-visibility-mixin.js`), não é mais
    //    marcada. `vitrine` é o efeito vivo deste filtro: ela continua na lista
    //    (inerte, esmaecida) e sem isso inflaria o índice — no CRM, uma vitrine
    //    antes de uma opcional em foco empurraria a 3ª linha para "Etapa 4".
    // 2. `moduloAteNivel` corta os NÍVEIS À FRENTE (2026-08-10, medido em tela:
    //    o drawer dizia "Etapa 3 de 21" ao lado do card dizendo "6 de 10
    //    obrigatórias"). As 21 etapas do CRM se espalham por 3 níveis e o
    //    accordion só desenha as do alcance; as 11 do nível 3 ficam no bloco
    //    colapsado "Quando quiser ir além". Era a última classe escondida pelo
    //    accordion que ainda contava aqui.
    //
    // O card embutido do módulo mede progresso (denominador = TODAS as
    // obrigatórias); o drawer mede posição na sequência que a pessoa percorre.
    // Continuam sendo perguntas diferentes mesmo que hoje ambas excluam a
    // barrada pelo mesmo motivo (ela não está mais em `modulosVisiveis`).
    tasksDoGrupoVisivel() {
      const modulo = this.moduloVisivelDoGuia
      if (!modulo) return null
      const noAlcance = moduloAteNivel(modulo, this.nivelDeAlcance) || modulo
      const tasks = (Array.isArray(noAlcance.tasks) && noAlcance.tasks) || []
      const naListaPrincipal = (t) => {
        const meta = (t && t.metadata) || {}
        return !meta.indisponivel && !meta.vitrine
      }
      return isEssential(this.task)
        ? essentialTasksNaOrdemVisual(noAlcance).filter(naListaPrincipal)
        : tasks.filter((t) => !isEssential(t) && naListaPrincipal(t))
    },
    groupIndex() {
      if (!this.context) return -1
      const doGrupo = this.tasksDoGrupoVisivel
      if (doGrupo) return doGrupo.findIndex((t) => t.id === this.task.id)
      return isEssential(this.task)
        ? this.context.essentialIndex
        : this.context.optionalIndex
    },
    groupTotal() {
      if (!this.context) return 0
      const doGrupo = this.tasksDoGrupoVisivel
      if (doGrupo) return doGrupo.length
      return isEssential(this.task)
        ? this.context.essentialTotal
        : this.context.optionalTotal
    },
    // ★ Navegação sobre o catálogo VISÍVEL (2026-08-05, 404 reportado em tela
    // pelo owner). `context.prevTask`/`nextTask` vêm do `guideContext`, um getter
    // Vuex — e getter não tem `router`, então não conhece flag nenhuma e anda
    // pela lista COMPLETA. Resultado: no CRM, "Próxima etapa" levava direto a
    // "Configurar integração de Ligações" (`/crm/comunicacoes/ligacoes`, exige
    // CRM_LIGACOES) e o guard respondia 404 — com o próprio drawer aberto ao
    // lado ensinando os passos.
    //
    // Bloquear a linha no accordion não cobria isto: são DOIS caminhos até a
    // mesma etapa, e este não passa por lá. Mesma lição do gate de contrato, que
    // precisou ser aplicado no accordion E no CTA das telas de boas-vindas.
    //
    // `tasksNaOrdemVisual` sobre o módulo já filtrado é a mesma sequência que o
    // accordion desenha, então "próxima" no drawer e "próxima" na tela
    // concordam. Fallback para o `context`: sem store/router utilizáveis
    // (testes isolados), volta ao comportamento anterior em vez de travar a
    // navegação inteira.
    // A obrigatória barrada por contrato JÁ SOME de `modulosVisiveis`
    // (`filtrarModulosVisiveis`, journey-visibility-mixin.js) — navegar pela
    // lista visível não para nela. O que ainda precisa de filtro explícito aqui
    // é a VITRINE: ela continua na lista (inerte, esmaecida), e sem removê-la
    // "próxima etapa" levaria a uma linha que `startTask` recusa abrir no
    // accordion. Aqui saem as mesmas duas classes que `startTask` recusa —
    // indisponível (defensivo, hoje inalcançável) e vitrine —, para que os
    // dois caminhos até uma etapa concordem sobre o que é alcançável.
    //
    // A etapa ATUAL nunca é removida: o usuário pode ter chegado nela por um
    // link antigo, e tirá-la da lista zeraria o índice e travaria prev/next.
    tasksNavegaveis() {
      const modulo = this.moduloVisivelDoGuia
      if (!modulo) return null
      const atual = (this.task && this.task.id) || null
      const tasks = tasksNaOrdemVisual(modulo).filter((t) => {
        if (t.id === atual) return true
        const metadata = (t && t.metadata) || {}
        return !metadata.indisponivel && !metadata.vitrine
      })
      return tasks.length ? tasks : null
    },
    indiceNavegavel() {
      if (!this.tasksNavegaveis || !this.task) return -1
      return this.tasksNavegaveis.findIndex((t) => t.id === this.task.id)
    },
    prevTask() {
      if (this.indiceNavegavel > 0) {
        return this.tasksNavegaveis[this.indiceNavegavel - 1]
      }
      // Índice 0 é "não há anterior"; -1 é "não sei responder" e cai no getter.
      if (this.indiceNavegavel === 0) return null
      return (this.context && this.context.prevTask) || null
    },
    nextTask() {
      if (this.indiceNavegavel >= 0) {
        return this.tasksNavegaveis[this.indiceNavegavel + 1] || null
      }
      return (this.context && this.context.nextTask) || null
    },
    requiresTasks() {
      return (this.context && this.context.requiresTasks) || []
    },
    hasRequires() {
      return this.requiresTasks.length > 0
    },
    taskName() {
      return (this.task && this.task.name) || ''
    },
    taskIcon() {
      return this.task ? lucideToIcon(this.task.icon) : ''
    },
    metadata() {
      return (this.task && this.task.metadata) || {}
    },
    statusKey() {
      return this.metadata.status || 'todo'
    },
    instructions() {
      return Array.isArray(this.metadata.instructions)
        ? this.metadata.instructions
        : []
    },
    hasInstructions() {
      return this.instructions.length > 0
    },
    location() {
      return this.metadata.location || ''
    },
    effort() {
      return this.metadata.effort || ''
    },
    hasLocation() {
      return !!this.location
    },
    // Metadados compactados numa linha só: "Etapa X de Y · esforço · módulo".
    // Antes espalhados em stepLine + status + effort + location (4 linhas).
    // X de Y é DENTRO DO GRUPO (obrigatórias entre obrigatórias, opcionais
    // entre opcionais) — ver groupIndex/groupTotal.
    // ★ `groupIndex` é `-1` quando a etapa não está no grupo visível — o caso
    // real é a VITRINE alcançada por "Próxima etapa": a navegação percorre o
    // array COMPLETO (decisão do usuário, ver §6 do CLAUDE.md), mas
    // `tasksDoGrupoVisivel` a exclui via `isEssential`. Sem este guard,
    // `groupIndex + 1` vira 0 e o drawer anuncia "Etapa 0 de 12" com a barra
    // zerada — o usuário lê como progresso perdido.
    //
    // Omitir o par é mais honesto que inventar uma posição: a etapa realmente
    // não tem lugar nessa contagem. O resto da linha (esforço, módulo) segue.
    temPosicaoNoGrupo() {
      return this.groupTotal > 0 && this.groupIndex >= 0
    },
    metaLine() {
      const parts = []
      if (this.temPosicaoNoGrupo) {
        parts.push(`Etapa ${this.groupIndex + 1} de ${this.groupTotal}`)
      }
      if (this.effort) parts.push(this.effort)
      if (this.module) parts.push(this.module.module)
      return parts.join(' · ')
    },
    // Barra de POSIÇÃO na jornada (Etapa X de Y) — não é conclusão (o status é o
    // checkbox do rodapé). Fica separada do estado por isso. Acompanha o
    // contador: dentro do grupo, para texto e barra nunca divergirem.
    progressPercent() {
      if (!this.temPosicaoNoGrupo) return 0
      return Math.round(((this.groupIndex + 1) / this.groupTotal) * 100)
    },
    isLastStep() {
      return !this.nextTask
    },
    primaryLabel() {
      if (!this.isLastStep) return 'Próxima etapa'
      return this.voltaParaModulo ? VOLTAR_MODULO_RODAPE : VOLTAR_JORNADA_RODAPE
    },
    // ---- Voltar contextual (ver utils/guide-origin.js) ----
    // Default explícito: um store que não declare estes getters (teste antigo,
    // estado parcial) devolve undefined, e o `||` recai no comportamento de
    // sempre. É a 3ª camada de fail-open — as outras são SET_GUIDE e readGuide.
    origemDoGuia() {
      return this.$store.getters['centralConfig/guideOrigin'] || GUIDE_ORIGIN_JOURNEY
    },
    moduleKeyDoGuia() {
      return this.$store.getters['centralConfig/guideModuleKey'] || ''
    },
    rotaDoModulo() {
      return rotaInicialDoModulo(this.moduleKeyDoGuia)
    },
    // ★ Fonte ÚNICA da decisão: rótulo e handler leem daqui. Separar os dois
    // permitiria o botão dizer "Voltar ao início do módulo" e o clique cair na
    // home — por isso a rota entra na condição, e não só a origem.
    voltaParaModulo() {
      return this.origemDoGuia === GUIDE_ORIGIN_MODULE && !!this.rotaDoModulo
    },
    // O usuário já está numa tela DO módulo de origem?
    //
    // Detecta pelo prefixo do `name` da rota, que é hierárquico por módulo
    // (`fee-fixo.dashboard`, `fee-fixo.pipeline`, …). Módulo cujo `name` de
    // entrada não tem prefixo (`carteira`, `clientes`) só casa na própria rota;
    // nos demais casos isto devolve `false` e o botão navega — o comportamento
    // anterior, que é o fallback seguro.
    jaEstaNoModulo() {
      if (!this.rotaDoModulo) return false
      const atual = (this.$route && this.$route.name) || ''
      if (!atual) return false
      const raiz = this.rotaDoModulo.split('.')[0]
      return atual === raiz || atual.indexOf(`${raiz}.`) === 0
    },
    // Estando dentro do módulo, "voltar" é sair do guia — a tela do módulo já
    // está atrás do painel. Navegar para a Visão Geral tiraria o usuário da aba
    // em que ele estava trabalhando (ex.: Acompanhamento) sem nenhum ganho.
    // O rótulo acompanha: promete "ao início do módulo" só quando de fato navega.
    labelVoltar() {
      if (!this.voltaParaModulo) return VOLTAR_JORNADA_HEADER
      return this.jaEstaNoModulo ? VOLTAR_MODULO_RODAPE : VOLTAR_MODULO_HEADER
    },
    // Conclusão manual. O status vem do backend (statusKey); o checkbox só alterna
    // marcar/desmarcar — o front nunca recalcula o status. O estado do checkbox É
    // o status da etapa (não há mais pill de status separado).
    isDone() {
      return this.statusKey === 'done'
    },
    checkLabel() {
      return this.isDone ? 'Concluída' : 'Marcar como concluída'
    },
    // Autoria da conclusão (spec-005). Numa etapa de escopo `escritorio` quem
    // marcou pode ter sido outra pessoa — sem dizer quem fez, "concluída" vira
    // um fato sem dono. Nome puro: sem avatar, sem link, sem PII além do nome.
    concluidaPor() {
      return this.metadata.concluida_por || ''
    },
    concluidaEm() {
      const iso = this.metadata.concluida_em
      // Guard obrigatório: aawzDate(undefined) devolve AGORA (data válida), o
      // que carimbaria a etapa com a data de hoje.
      if (!iso) return ''
      const data = aawzDate(iso)
      return data.isValid() ? data.format('DD/MM/YYYY') : ''
    },
    // Linha montada aqui, não no template (regra da casa: nada de chamada de
    // função em interpolação). Sem data utilizável a linha inteira some — o
    // checkbox marcado já diz "concluída", e a autoria só existe para responder
    // quem/quando; sem o quando, não sobra o que dizer.
    autoriaLinha() {
      if (!this.isDone || !this.concluidaEm) return ''
      const template = this.concluidaPor ? AUTORIA : AUTORIA_SEM_AUTOR
      return template
        .replace('{autor}', this.concluidaPor)
        .replace('{data}', this.concluidaEm)
    },
    // `null` remove o atributo (Vue) — aria-describedby apontando para um id que
    // não está no DOM é referência morta para o leitor de tela.
    autoriaId() {
      return this.autoriaLinha ? 'cc-autoria' : null
    },
  },
  watch: {
    // Enquanto o guia está ativo (usuário percorrendo a jornada), liga o refresh
    // (foco + poll) e o atalho Esc; ao fechar, desliga. O drawer é global e
    // sempre montado, por isso o gatilho é o estado ativo, não o mount.
    guideActive(active) {
      if (active) {
        this.startJourneyRefresh()
        document.addEventListener('keydown', this.onKeydown)
      } else {
        this.stopJourneyRefresh()
        document.removeEventListener('keydown', this.onKeydown)
      }
    },
  },
  created() {
    // O guia pode ser restaurado aberto (localStorage) apos um reload FORA da
    // home — e a jornada so e carregada por view-home.created(). Sem isso, o
    // painel abriria vazio (guideContext null) com o layout deslocado. Garante
    // o load quando o guia esta ativo mas a jornada ainda nao carregou.
    if (this.needsJourneyLoad) {
      this.loadContext()
    }
    // Transição "Iniciar etapa" (a11y): router/index.js emite este evento no
    // afterEach da navegação disparada por startTask() no accordion — move o
    // foco de teclado pro painel (drawer) pra não perder o contexto após o
    // redirect.
    GlobalBus.$on(CENTRAL_CONFIG_FOCUS_GUIDE, this.focus)
  },
  mounted() {
    // Guia restaurado já aberto no boot: o watch de guideActive não dispara
    // (sem mudança), então liga o refresh e o atalho Esc aqui.
    if (this.guideActive) {
      this.startJourneyRefresh()
      document.addEventListener('keydown', this.onKeydown)
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
    GlobalBus.$off(CENTRAL_CONFIG_FOCUS_GUIDE, this.focus)
  },
  methods: {
    // Move o foco pro painel (root focável via tabindex="-1") — chamado
    // externamente após a navegação da transição "Iniciar etapa". Sem efeito
    // se o painel não está aberto (ex.: evento de uma navegação não relacionada
    // à jornada — não deveria acontecer, mas o guard evita foco perdido).
    focus() {
      if (this.panelOpen && this.$refs.panel) this.$refs.panel.focus()
    },
    // Esc recolhe o painel pro FAB (não fecha/cancela) — o guia é persistente,
    // não um modal descartável; recolher é a ação não-destrutiva equivalente
    // ao botão "Recolher" do header.
    onKeydown(e) {
      if (e.key === 'Escape' && this.panelOpen) this.onCollapse()
    },
    // Carrega o contexto quando o guia é restaurado fora da home. Sem
    // loading/erro aqui, uma falha de rede deixava o painel eternamente vazio
    // (só o header) — sem spinner, sem mensagem, sem retry.
    async loadContext() {
      this.loadingContext = true
      this.loadError = false
      const r = await apiCentralConfig.carregarJornada()
      const ok = !!(r && r.status === 200)
      this.loadError = !ok
      this.loadingContext = false
      // Auto-cura: jornada carregou (200) e mesmo assim não há contexto — o
      // `taskId` persistido não existe mais no catálogo (troca de escritório,
      // re-curadoria, etapa removida). Sem isto, `hasContext`, `loadingContext`
      // e `loadError` ficam TODOS falsos: painel de 380px aberto e vazio,
      // deslocando o app e capando modais (_modal.sass) até o usuário fechar na
      // mão. Fechar o guia é o estado honesto — não há etapa para guiar.
      if (ok && !this.hasContext) {
        this.$store.dispatch('centralConfig/closeGuide')
      }
    },
    navigate(url) {
      if (!url) return
      // NavigationDuplicated é benigno (destino igual à rota atual).
      this.$router.push(url).catch(() => {})
    },
    // Header (recolher): minimiza o painel para o FAB (bolinha) no canto inferior
    // direito, mantendo a tarefa ativa e o usuário na tela de destino.
    onCollapse() {
      this.$store.dispatch('centralConfig/collapseGuide')
    },
    // FAB: reabre o painel lateral.
    onExpand() {
      this.$store.dispatch('centralConfig/expandGuide')
    },
    // Fecha o guia por completo (não recolhe pro FAB) e cancela o fluxo da
    // jornada — o usuário permanece na tela atual. Reabrível pela home da jornada.
    onClose() {
      this.$store.dispatch('centralConfig/closeGuide')
    },
    // "Voltar para a jornada": fecha o drawer e retorna à home da Central.
    // Re-sincroniza antes de voltar — é o caminho que goNext usa na ÚLTIMA
    // etapa de um módulo (via return antecipado), então sem o refresh aqui a
    // home podia mostrar o módulo "incompleto" por até ~60s (próximo poll)
    // mesmo o usuário tendo acabado de concluir tudo.
    backToJourney() {
      this.refreshJourney()
      // Antes de fechar (closeGuide zera moduleId/taskId), registra de onde o
      // usuário saiu: a home reabre naquele ponto em vez de jogá-lo no topo.
      // Não dá para depender do `savedPosition` do router — `push` não é
      // navegação de histórico —, e a altura da lista muda quando a etapa vira
      // concluída, então ancorar no módulo é mais estável que restaurar pixels.
      // A ETAPA vai junto porque é ela que define o nível: um módulo com etapas
      // em níveis diferentes aparece em mais de uma seção da home.
      if (this.module && this.module.id) {
        this.$store.dispatch('centralConfig/setReturnModule', {
          moduleId: this.module.id,
          taskId: this.task && this.task.id,
        })
      }
      this.$store.dispatch('centralConfig/closeGuide')
      this.$router.push({ name: HOME_ROUTE }).catch(() => {})
    },
    // "Voltar ao início do módulo": mesmo gesto, destino diferente — o usuário
    // entrou pela barra de progresso de dentro do módulo e volta para lá.
    //
    // ⚠️ Ao contrário de backToJourney, NÃO despacha `setReturnModule`. Aquele
    // estado é de uso único, só a home o consome e o limpa, e ele não é
    // persistido: gravá-lo sem ir para a home o deixa pendurado em memória até o
    // usuário abrir a Jornada por qualquer outro caminho — quando ela trocaria
    // de nível e rolaria até um módulo que ele visitou noutro fluxo.
    //
    // O refresh fica, e aqui pesa mais que no caminho da jornada: o destino é a
    // tela que hospeda a barra de progresso, cujo contador precisa já refletir a
    // etapa recém-concluída.
    backToModule() {
      this.refreshJourney()
      this.$store.dispatch('centralConfig/closeGuide')
      // Já dentro do módulo: fechar o painel JÁ devolve o usuário à tela dele.
      // Navegar aqui o tiraria da aba em que estava (Acompanhamento, Clientes…)
      // para jogá-lo na Visão Geral, sem nada a ganhar.
      if (this.jaEstaNoModulo) return
      this.$router.push({ name: this.rotaDoModulo }).catch(() => {})
    },
    // Único ponto de entrada do gesto "voltar" — header e rodapé passam por aqui
    // para não divergirem.
    voltarDoGuia() {
      if (this.voltaParaModulo) {
        this.backToModule()
        return
      }
      this.backToJourney()
    },
    // Spinner breve em goPrev/goNext (Fix 8): as rotas de destino são
    // lazy-loaded de outros módulos — sem isso, o clique parece sem resposta
    // enquanto o chunk baixa. Sem callback de conclusão do carregamento do
    // chunk, usa um mínimo fixo (mesmo padrão dos outros loadings do módulo).
    startNavigating() {
      this.navigating = true
      setTimeout(() => {
        this.navigating = false
      }, NAVIGATING_DELAY_MS)
    },
    // Ponto único de troca de etapa — anterior, próxima e o avanço automático da
    // conclusão passam por aqui, para os três não divergirem no que gravam na
    // sessão do guia.
    //
    // O destino chega por PARÂMETRO de propósito: `openGuide` move o currentTask
    // e `prevTask`/`nextTask` são computeds derivados dele, então reler qualquer
    // um deles depois do dispatch apontaria para a etapa seguinte-da-seguinte
    // (defasagem de 1). Quem chama resolve o alvo antes.
    abrirEtapa(target) {
      this.startNavigating()
      this.$store.dispatch('centralConfig/openGuide', {
        moduleId: this.module.id,
        taskId: target.id,
        // A origem é da SESSÃO do guia, não da etapa: andar entre etapas não
        // muda por onde o usuário entrou, nem para onde o voltar leva.
        origin: this.origemDoGuia,
        moduleKey: this.moduleKeyDoGuia || null,
      })
      this.navigate(target.url)
    },
    goPrev() {
      if (this.navigating || !this.prevTask || !this.module) return
      this.abrirEtapa(this.prevTask)
    },
    goNext() {
      if (this.navigating) return
      if (this.isLastStep) {
        this.voltarDoGuia()
        return
      }
      if (!this.module || !this.nextTask) return
      // Fire-and-forget: o usuário acabou de trabalhar a etapa atual; ao avançar,
      // re-consulta o backend para o status dela chegar curado (Fase 2).
      this.refreshJourney()
      this.abrirEtapa(this.nextTask)
    },
    // ★ Concluir já avança (2026-08-10, reportado em tela pelo owner). Marcar É o
    // gesto de "terminei aqui" — o avanço é a continuação dele, não um segundo
    // comando: antes o usuário ficava parado na etapa cumprida, com o passo a
    // passo que ele acabou de executar ainda na frente.
    //
    // Sem refreshJourney aqui, ao contrário do goNext: `marcarTask` já re-buscou
    // a jornada com `forceUpdate` antes de resolver, e repetir seria uma segunda
    // request ao backend no mesmo instante.
    //
    // ⚠️ Na ÚLTIMA etapa não faz nada, de propósito. O equivalente a "avançar"
    // ali seria fechar o guia — a única ação do painel que o usuário não pediu, e
    // irreversível no gesto (reabrir exige voltar à home da Jornada). O rodapé já
    // oferece a saída com o rótulo certo, via `primaryLabel`.
    avancarAposConcluir() {
      if (this.isLastStep || !this.module || !this.nextTask) return
      setTimeout(() => {
        // Reavalia tudo no disparo: nos 450ms o usuário pode ter recolhido o
        // painel, fechado o guia ou clicado "Próxima etapa" na mão. Avançar por
        // cima disso o tiraria de onde ele escolheu estar — e o guard de
        // `navigating` é o que impede o clique manual virar pulo de duas etapas.
        if (!this.panelOpen || this.navigating) return
        if (!this.module || !this.nextTask) return
        this.abrirEtapa(this.nextTask)
      }, AUTO_ADVANCE_DELAY_MS)
    },
    // Marca/desmarca manualmente a etapa atual. O re-fetch dentro de marcarTask
    // (forceUpdate) re-sincroniza o status no store — o front não o recalcula.
    async toggleTaskStatus() {
      if (this.marking || !this.task) return
      // Guard no MÉTODO, não só no `:disabled` — mesmo motivo do accordion.
      if (this.tarefaEhVitrine) return
      // Lido ANTES do request: `marcarTask` re-busca a jornada e o `isDone` já
      // reflete o novo status quando o await volta — reler ali diria sempre
      // "marcou", inclusive no gesto de desmarcar.
      const marcando = !this.isDone
      this.marking = true
      const minDelay = new Promise((resolve) => setTimeout(resolve, MIN_LOADING_MS))
      let ok = false
      try {
        const r = await apiCentralConfig.marcarTask({
          taskId: this.task.id,
          done: marcando,
        })
        ok = !!(r && r.status === 200)
        // ★ Sucesso NÃO vai para o snack (2026-08-10, reportado em tela pelo
        // owner). Ele nasce no canto inferior direito com 8s de timeout
        // (state/modules/feedback/actions.js) e o drawer ocupa exatamente essa
        // quina: o "Task atualizada." cobria o próprio checkbox recém-clicado e
        // os botões anterior/próxima. Dentro do painel o sucesso já se anuncia
        // três vezes — check verde, linha de autoria, avanço de etapa —, então o
        // snack só somava ruído por cima dos controles.
        //
        // A FALHA continua nele: é o único sinal que sobra: sem snack o check
        // volta ao vazio e o gesto parece não ter sido registrado.
        if (!ok) this.$showSnack(r)
      } finally {
        await minDelay
        this.marking = false
      }
      // Só o gesto de MARCAR avança. Desmarcar é correção de engano — arrastar o
      // usuário adiante o tiraria justamente da etapa que ele voltou para revisar.
      if (ok && marcando) this.avancarAposConcluir()
    },
  },
}
</script>

<template>
  <div>
  <transition name="cc-drawer-slide">
    <aside
      v-if="panelOpen"
      ref="panel"
      class="cc-drawer"
      aria-label="Guia de configuração"
      tabindex="-1"
    >
      <div class="cc-drawer__inner">
        <!-- Header numa linha só: voltar + título (esquerda) | recolher + fechar
             (direita). Antes o "Voltar" ficava numa linha própria (degrau visual)
             e o marcar competia aqui — agora tudo que é chrome de navegação está
             agrupado, e a conclusão foi pro rodapé. -->
        <header class="cc-drawer__header">
          <button
            type="button"
            class="cc-drawer__icon-btn"
            :aria-label="labelVoltar"
            :title="labelVoltar"
            @click="voltarDoGuia"
          >
            <icon-svg name="mdi-arrow-left" :size="20" color="var(--neutral-600)" />
          </button>
          <span class="cc-drawer__eyebrow">Jornada de configuração</span>
          <span class="cc-drawer__hspacer" />
          <button
            type="button"
            class="cc-drawer__icon-btn"
            aria-label="Recolher guia"
            title="Recolher"
            @click="onCollapse"
          >
            <icon-svg name="mdi-arrow-collapse-right" :size="18" color="var(--neutral-600)" />
          </button>
          <button
            type="button"
            class="cc-drawer__icon-btn"
            aria-label="Fechar e cancelar a jornada"
            title="Fechar"
            @click="onClose"
          >
            <icon-svg name="mdi-close" :size="20" color="var(--neutral-600)" />
          </button>
        </header>

        <!-- Aviso discreto (Fix 9): falha persistente do refresh de fundo
             (poll/foco) — sem isso, a jornada "congela" sem nenhum sinal.
             Some sozinho no próximo sucesso. -->
        <p v-if="journeyStale" class="az-ds-aux cc-stale">
          Sincronização desatualizada — atualize a página.
        </p>

        <div v-if="hasContext" class="cc-drawer__scroll">
          <!-- Identidade compacta: ícone + título + 1 linha de metadados -->
          <div class="cc-task">
            <IconWithBackground
              :container-size="40"
              :icon="taskIcon"
              :icon-size="20"
              color="primary"
            />
            <div class="cc-task__body">
              <span class="az-ds-card-title cc-task__name">
                {{ taskName }}
              </span>
              <span class="az-ds-aux cc-task__meta">
                {{ metaLine }}
              </span>
            </div>
          </div>

          <!-- Barra de posição na jornada (Etapa X de Y). Some junto com o
               contador quando a etapa não pertence ao grupo visível: uma barra
               zerada ali seria lida como progresso perdido. -->
          <div v-if="temPosicaoNoGrupo" class="cc-progress">
            <span class="cc-progress__fill" :style="{ width: progressPercent + '%' }" />
          </div>

          <!-- Vitrine: diz por que a etapa está aqui sem poder ser feita. Vem
               ANTES do passo a passo, que continua legível — é ele que mostra o
               que o recurso faz, e é esse o propósito de exibir a etapa. -->
          <p v-if="tarefaEhVitrine" class="cc-vitrine-aviso">
            <icon-svg name="mdi-lock-outline" :size="14" color="var(--neutral-600)" />
            <span>{{ vitrineTooltip }}</span>
          </p>

          <!-- Breadcrumb da tela vira chip clicável que LEVA à tela da etapa
               (antes era texto morto ocupando espaço). Na vitrine ele sai: a
               tela existe, mas o recurso dentro dela não está contratado. -->
          <button
            v-if="hasLocation && !tarefaEhVitrine"
            type="button"
            class="cc-gochip"
            @click="navigate(task.url)"
          >
            <icon-svg name="mdi-open-in-new" :size="14" color="var(--primary)" />
            <span class="cc-gochip__text">Ir para {{ location }}</span>
          </button>

          <CentralConfigStepGuide
            v-if="hasInstructions"
            :instructions="instructions"
            class="cc-block"
          />

          <div v-if="hasRequires" class="az-card-bordered rounded-sm cc-requires-card cc-block">
            <span class="az-ds-label cc-requires-title">PRÉ-REQUISITOS</span>
            <ul class="cc-requires">
              <li
                v-for="require in requiresTasks"
                :key="require.id"
                class="cc-requires__item"
              >
                <icon-svg
                  v-if="require.isDone"
                  name="check-circle"
                  :size="16"
                  color="var(--success)"
                />
                <span v-else class="cc-dot cc-dot--todo" />
                <span class="az-ds-card-title">{{ require.name }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Guia restaurado fora da home ainda carregando o contexto. -->
        <div v-else-if="loadingContext" class="cc-drawer__scroll">
          <BaseLoadingSkeleton height="48px" class="mb-3" />
          <BaseLoadingSkeleton height="4px" class="mb-3" />
          <BaseLoadingSkeleton height="80px" />
        </div>

        <!-- Falha ao carregar o contexto (rede/500) — antes ficava em branco
             pra sempre, sem spinner/erro/retry. -->
        <div v-else-if="loadError" class="cc-drawer__scroll cc-drawer__state">
          <p class="az-ds-body">
            Não foi possível carregar esta etapa. Tente novamente ou contate o
            suporte se o problema persistir.
          </p>
          <BaseButton primary @click="loadContext">Tentar novamente</BaseButton>
        </div>

        <!-- Autoria da conclusão (spec-005): quem marcou a etapa e quando.
             Fica ENTRE o scroll e o rodapé (molde do .cc-stale, do outro lado
             do scroll) porque precisa estar visível exatamente quando o usuário
             olha o checkbox que ela explica — no fim do scroll sairia de vista
             com o passo a passo longo. NÃO é um terceiro estado de status. -->
        <p v-if="autoriaLinha" id="cc-autoria" class="az-ds-aux cc-autoria">
          {{ autoriaLinha }}
        </p>

        <!-- Footer: conclusão (checkbox, esquerda — o estado do checkbox É o
             status) + navegação (direita). Ação de concluir pertence ao fim da
             tarefa, não antes dela. -->
        <footer v-if="hasContext" class="cc-drawer__footer">
          <button
            type="button"
            class="cc-check"
            :class="{ 'cc-check--done': isDone }"
            :disabled="marking || tarefaEhVitrine"
            :aria-pressed="isDone ? 'true' : 'false'"
            :aria-describedby="autoriaId"
            @click="toggleTaskStatus"
          >
            <span class="cc-check__box">
              <v-progress-circular
                v-if="marking"
                indeterminate
                :size="13"
                :width="2"
                color="primary"
              />
              <icon-svg
                v-else-if="isDone"
                name="mdi-check"
                :size="13"
                color="var(--white)"
              />
            </span>
            <span>{{ checkLabel }}</span>
          </button>
          <span class="cc-drawer__hspacer" />
          <BaseButton
            v-if="prevTask"
            bordered
            min-width="0"
            :loading="navigating"
            aria-label="Etapa anterior"
            class="cc-drawer__prev"
            @click="goPrev"
          >
            <icon-svg name="mdi-arrow-left" :size="18" color="var(--neutral-600)" />
          </BaseButton>
          <BaseButton
            primary
            min-width="0"
            :loading="navigating"
            class="cc-drawer__next"
            @click="goNext"
          >
            {{ primaryLabel }}
          </BaseButton>
        </footer>
      </div>
    </aside>
  </transition>

    <!-- FAB da jornada: aparece quando o painel é recolhido; fica no canto
         inferior direito junto ao dock de atalhos. Clicar reabre o painel. -->
    <transition name="cc-fab-pop">
      <button
        v-if="fabOpen"
        type="button"
        class="cc-fab"
        aria-label="Reabrir jornada de configuração"
        @click="onExpand"
      >
        <IconWithBackground
          :container-size="30"
          icon="mdi-compass-outline"
          :icon-size="30"
          color="white"
          transparent
        />
      </button>
    </transition>
  </div>
</template>

<style lang="sass" scoped>
.cc-drawer
  position: fixed
  top: 0
  right: 0
  bottom: 0
  width: 380px
  // Acima da pilha de v-dialog do Vuetify 2 (activeZIndex ~200+, incrementa por
  // dialog empilhado) para o guia da Jornada ficar VISÍVEL ao lado do modal
  // (ex.: novo cliente), não coberto por ele. Abaixo de toasts/snackbars.
  z-index: 240
  background: var(--white)
  border-left: 1px solid var(--gray-3)
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.06)

// Mobile/tablet estreito: 380px é maior que a viewport inteira — o painel
// cobre a tela toda; o FAB usa o offset padrão do dock desktop (right:222px),
// que não existe nesse breakpoint.
@media (max-width: 599px)
  .cc-drawer
    width: 100vw

  .cc-fab
    right: 16px

.cc-drawer__inner
  display: flex
  flex-direction: column
  height: 100%

// Header numa linha: voltar + título + espaço + recolher + fechar.
.cc-drawer__header
  display: flex
  align-items: center
  gap: 6px
  padding: 10px 12px
  border-bottom: 1px solid var(--border-hairline)

.cc-drawer__hspacer
  flex: 1 1 auto

.cc-drawer__eyebrow
  font-family: var(--font-family-paragraph)
  font-size: 13px
  font-weight: 600
  color: var(--neutral-600)
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis
  min-width: 0

.cc-drawer__icon-btn
  display: flex
  align-items: center
  justify-content: center
  width: 28px
  height: 28px
  border-radius: 8px
  background: transparent
  cursor: pointer
  flex-shrink: 0

  &:hover
    background: var(--neutral-100)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

.cc-drawer__scroll
  flex: 1 1 auto
  min-height: 0
  overflow-y: auto
  padding: 16px 20px 20px 20px

// Estado de erro do load do contexto (Fix 1) — mensagem + retry, sem o
// esqueleto do conteúdo real.
.cc-drawer__state
  display: flex
  flex-direction: column
  align-items: flex-start
  gap: 12px

// Aviso de sincronização desatualizada (Fix 9) — discreto, sem cor de alerta
// (não é erro bloqueante, só um sinal de que o refresh de fundo está falhando).
.cc-stale
  margin: 0
  padding: 8px 20px
  background: var(--neutral-100)
  border-bottom: 1px solid var(--border-hairline)

// Autoria da conclusão (spec-005) — irmã do rodapé dentro de .cc-drawer__inner
// (flex column), então não rola junto com o passo a passo. Espelha .cc-stale
// invertendo a borda: lá separa do header acima, aqui do rodapé abaixo.
.cc-autoria
  margin: 0
  padding: 8px 20px
  color: var(--neutral-600)
  border-top: 1px solid var(--border-hairline)

.cc-task
  display: flex
  align-items: flex-start
  gap: 12px

.cc-task__body
  display: flex
  flex-direction: column
  gap: 3px
  min-width: 0

.cc-task__name
  line-height: 1.3

.cc-task__meta
  line-height: 1.4

.cc-progress
  height: 4px
  border-radius: 2px
  background: var(--neutral-200)
  margin-top: 14px
  overflow: hidden

.cc-progress__fill
  display: block
  height: 100%
  border-radius: 2px
  background: var(--primary)
  transition: width 0.3s ease

// Vitrine: diz por que a etapa aparece sem poder ser executada.
.cc-vitrine-aviso
  display: flex
  align-items: flex-start
  gap: 8px
  margin: 0 0 16px
  padding: 10px 12px
  border-radius: var(--radius-sm)
  background: var(--neutral-50)
  font-family: var(--font-family-paragraph)
  font-size: 12px
  line-height: 1.45
  color: var(--neutral-600)

// Breadcrumb → chip clicável que leva à tela da etapa.
.cc-gochip
  display: inline-flex
  align-items: center
  gap: 6px
  max-width: 100%
  margin-top: 14px
  padding: 6px 12px
  border-radius: 8px
  border: 1px solid var(--border-hairline)
  background: var(--white)
  font-family: var(--font-family-paragraph)
  font-size: 12px
  font-weight: 600
  color: var(--primary)
  cursor: pointer

  &:hover
    background: var(--success-bg-soft)
    border-color: transparent

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

.cc-gochip__text
  overflow: hidden
  text-overflow: ellipsis
  white-space: nowrap

.cc-requires-card
  display: flex
  flex-direction: column
  padding: 16px

.cc-requires
  list-style: none
  margin: 10px 0 0 0
  padding: 0

.cc-requires__item
  display: flex
  align-items: center
  gap: 8px
  padding: 8px 0

  & + .cc-requires__item
    border-top: 1px solid var(--border-hairline)

.cc-dot
  width: 14px
  height: 14px
  border-radius: 50%
  box-sizing: border-box
  flex-shrink: 0

.cc-dot--todo
  border: 2px solid var(--neutral-400)
  background: var(--white)

// Footer: checkbox de conclusão (esquerda) + navegação (direita).
.cc-drawer__footer
  display: flex
  align-items: center
  gap: 8px
  padding: 12px 16px
  border-top: 1px solid var(--border-hairline)

// Checkbox-toggle de conclusão: o box marcado É o status (verde+check); vazio =
// pendente. Substitui o antigo pill de status + botão de marcar (dois elementos
// que pareciam irmãos mas um era info e outro comando).
.cc-check
  display: inline-flex
  align-items: center
  gap: 8px
  padding: 6px 4px
  background: transparent
  border: none
  font-family: var(--font-family-paragraph)
  font-size: 13px
  font-weight: 500
  color: var(--neutral-600)
  cursor: pointer

  &:hover:not(:disabled) .cc-check__box
    border-color: var(--success)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px
    border-radius: 6px

.cc-check--done
  color: var(--success-text)

.cc-check__box
  display: flex
  align-items: center
  justify-content: center
  width: 18px
  height: 18px
  border-radius: 5px
  border: 2px solid var(--neutral-400)
  background: var(--white)
  flex-shrink: 0
  transition: background 0.15s ease, border-color 0.15s ease

.cc-check--done .cc-check__box
  background: var(--success)
  border-color: var(--success)

.cc-drawer__prev
  flex: 0 0 auto

.cc-drawer__next
  flex: 0 0 auto

.cc-drawer-slide-enter-active,
.cc-drawer-slide-leave-active
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)

.cc-drawer-slide-enter,
.cc-drawer-slide-leave-to
  transform: translateX(100%)

.cc-block
  margin-top: 18px

// FAB da jornada (quando recolhido) — mesma "bolinha" dos atalhos flutuantes.
// Não há componente base de FAB no projeto; replicamos a geometria do
// chat-ia-pop-up (__icon): 71px, border-radius 100%, hover opacity .7. A sombra
// NÃO copia o hex legado daquele componente — usa o token de elevação flutuante
// do DS (--shadow-pop, nível "menus/popovers" em src/design/_controls.sass),
// que é a camada a que um FAB pertence. Fica à esquerda do dock (chat-ia/
// atendimento em right:40..100px, 71px de largura → ocupam até ~171px) na mesma
// linha (bottom:16px); z-index 241 fica acima dos v-dialog do Vuetify de
// propósito, para o FAB seguir clicável com um modal aberto.
.cc-fab
  position: fixed
  right: 222px
  bottom: 16px
  z-index: 241
  box-sizing: border-box
  width: 71px
  height: 71px
  min-width: 71px
  padding: 0
  border-radius: 100%
  border: none
  background: var(--primary)
  box-shadow: var(--shadow-pop)
  display: flex
  align-items: center
  justify-content: center
  cursor: pointer

  &:hover
    opacity: 0.7

  &:focus-visible
    outline: 2px solid var(--white)
    outline-offset: 2px

.cc-fab-pop-enter-active,
.cc-fab-pop-leave-active
  transition: transform 0.2s ease, opacity 0.2s ease

.cc-fab-pop-enter,
.cc-fab-pop-leave-to
  transform: scale(0.6)
  opacity: 0
</style>
